import type { ReactNode } from 'react';
import { Bell, UserCircle2 } from 'lucide-react';
import { RaceBib } from '@/components/foundry';
import KickstartV2Footer from './KickstartV2Footer';

interface Props {
  children: ReactNode;
  onHomeClick?: () => void;
}

/**
 * KickstartV2Shell — minimal top-bar shell.
 * Kickstart is a linear discovery flow, not a multi-route operator
 * tool, so no left rail. Top bar holds brand + cohort meta + account.
 */
export default function KickstartV2Shell({ children, onHomeClick }: Props) {
  return (
    <div
      className="min-h-[100dvh] bg-fd-bg text-fd-text font-geist antialiased"
      style={{
        backgroundImage:
          'linear-gradient(var(--fd-grid) 1px, transparent 1px),' +
          'linear-gradient(90deg, var(--fd-grid) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        backgroundPosition: '-1px -1px',
      }}
    >
      <header
        className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b-[1.5px] border-fd-text px-4 sm:px-6 lg:px-8"
        style={{ background: 'color-mix(in oklch, var(--fd-bg) 92%, transparent)', backdropFilter: 'saturate(140%) blur(10px)' }}
      >
        {/* Brand mark — clicks back to home if handler provided */}
        <button
          type="button"
          onClick={onHomeClick}
          className="flex items-center gap-2.5 shrink-0 transition-opacity hover:opacity-80"
        >
          <span
            className="grid h-8 w-8 place-items-center rounded-[4px] border-[1.5px] border-fd-text bg-fd-text font-geist-mono text-[16px] font-bold text-fd-accent"
            style={{ boxShadow: '3px 3px 0 0 var(--fd-accent)' }}
          >
            A
          </span>
          <div className="leading-tight text-left">
            <div className="text-[14px] font-bold uppercase tracking-[-0.005em] text-fd-text">AI Brand Lab</div>
            <div className="hidden font-geist-mono text-[10px] font-semibold uppercase tracking-[0.10em] text-fd-text-muted sm:block">
              Kickstart · Cohort 04
            </div>
          </div>
        </button>

        <div className="flex-1" />

        {/* Cohort cutoff status — race-bib pill */}
        <RaceBib n="" label="Cohort closes Friday" tone="race" size="md" shadow="sm" className="hidden md:inline-flex" />

        <button
          aria-label="Notifications"
          className="grid h-8 w-8 place-items-center rounded-[6px] text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
        >
          <Bell className="h-4 w-4" />
        </button>

        <button
          aria-label="Account"
          className="inline-flex h-8 items-center gap-1.5 rounded-[6px] px-2.5 font-geist text-[12.5px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text"
        >
          <UserCircle2 className="h-4 w-4" />
          <span className="hidden sm:inline">Pamela</span>
        </button>
      </header>

      <main>{children}</main>
      <KickstartV2Footer />
    </div>
  );
}
